import { Children, ReactNode, useMemo } from "react";
import classNames from "classnames";
import { useLocation } from "react-router-dom";
import { StateAwareNavLink } from "@components/routes/StateAwareNavLink";
import { searchWithParams } from "@components/auditLog/chat/TaskSummaryLinks";
import { getOpenTaskLocationState } from "@components/tasks/useOpenTaskUrl";
import { HStack } from "@design-system/HStack/HStack";
import { Message, MessageType } from "@common/types/chat";
import { Button } from "@design-system/Button/Button";
import { Markdown } from "@design-system/Markdown/Markdown";
import {
    EntityRefPill,
    LinkPill,
    type LinkPillProps,
} from "../slate/entityRef/EntityRefPill";
import { SlateRenderer } from "../slate/SlateRenderer";
import { SlateEditor } from "../slate/SlateEditor";
import { SlateValue, createEmptySlateValue } from "../slate/slateTypes";
import { deserializeSlateValue, slateToPlainText } from "../slate/slateHelpers";
import { MessageAuthor } from "../messageAuthor";
import { InviteAccountantMessage } from "./InviteAccountantMessage";
import { ReceiptMatchingMessage } from "./ReceiptMatchingMessage";
import styles from "../MessageBubble.module.scss";

interface AgentMarkdownLinkProps {
    href?: string;
    children?: ReactNode;
}

const OPEN_PREFIX = "/open?";

// A task link opens in place on the page the reader is on; every other
// target goes through the /open route, which resolves the destination.
// Older comments hold query-only task links `?taskId=…`.
function inPlaceTaskId({
    href,
    openParams,
}: {
    href: string;
    openParams: URLSearchParams | null;
}): string | null {
    if (href.startsWith("?")) {
        return new URLSearchParams(href).get("taskId");
    }
    return openParams?.get("target") === "task"
        ? openParams.get("taskId")
        : null;
}

function linkPillRef(
    openParams: URLSearchParams | null,
): Pick<LinkPillProps, "kind" | "id"> | null {
    const target = openParams?.get("target");
    if (target === "transaction") {
        const id = openParams?.get("transactionId");
        return id ? { kind: "transaction", id } : null;
    }
    if (target === "journalEntry") {
        const id = openParams?.get("journalEntryId");
        return id ? { kind: "journalEntry", id } : null;
    }
    return null;
}

function linkLabel(children: ReactNode, fallback: string): string {
    const text = Children.toArray(children)
        .filter((child): child is string => typeof child === "string")
        .join("");
    return text || fallback;
}

// Agent links are `/open?target=…` (task, transaction, journalEntry).
// Anything else is an outside address.
const AgentMarkdownLink = ({ href = "", children }: AgentMarkdownLinkProps) => {
    const { search, state } = useLocation();
    const openParams = href.startsWith(OPEN_PREFIX)
        ? new URLSearchParams(href.slice(OPEN_PREFIX.length))
        : null;
    const taskId = inPlaceTaskId({ href, openParams });
    if (taskId !== null) {
        return (
            <EntityRefPill
                entityRef={{
                    kind: "task",
                    id: taskId,
                    label: linkLabel(children, taskId),
                }}
                link={{
                    to: {
                        search: searchWithParams({
                            search,
                            params: `taskId=${taskId}`,
                        }),
                    },
                    replace: true,
                    state: getOpenTaskLocationState(state),
                }}
            />
        );
    }
    const pillRef = linkPillRef(openParams);
    if (pillRef !== null) {
        return (
            <LinkPill
                {...pillRef}
                label={linkLabel(children, pillRef.id)}
                to={href}
            />
        );
    }
    if (openParams !== null) {
        return <StateAwareNavLink to={href}>{children}</StateAwareNavLink>;
    }
    return (
        <a href={href} target="_blank" rel="noopener noreferrer">
            {children}
        </a>
    );
};

const AGENT_MARKDOWN_OPTIONS = { overrides: { a: AgentMarkdownLink } };

interface Props {
    message: Message;
    author: MessageAuthor;
    isEditing?: boolean;
    onSave?: (value: SlateValue) => void;
    onCancel?: () => void;
    onToggleCheck?: (path: number[]) => void;
}

export const CustomMessageBubbleContent = ({
    message,
    author,
    isEditing = false,
    onSave,
    onCancel,
    onToggleCheck,
}: Props) => {
    const initialValue = useMemo<SlateValue>(() => {
        if (message.type !== MessageType.SLATE) {
            return createEmptySlateValue();
        }
        try {
            return deserializeSlateValue(message.content);
        } catch {
            return createEmptySlateValue();
        }
    }, [message.content, message.type]);

    if (message.type === MessageType.ONBOARDING_INVITE_ACCOUNTANT_OPENING) {
        return <InviteAccountantMessage />;
    }

    if (message.type === MessageType.ONBOARDING_RECEIPT_MATCHING_OPENING) {
        return <ReceiptMatchingMessage />;
    }

    if (message.type === MessageType.SLATE) {
        if (isEditing) {
            return (
                <div className="w-100">
                    <SlateEditor
                        value={initialValue}
                        onSubmit={onSave}
                        placeholder="Edit message..."
                        autoFocus
                        initialOrganizationScopedMessage={
                            !!message.organizationId
                        }
                        isEditing
                    />
                    <HStack gap={8} justify="end" className="mt-2">
                        <Button variant="primary" size="sm" onClick={onCancel}>
                            Cancel
                        </Button>
                    </HStack>
                </div>
            );
        }
        if (author.kind === "agent") {
            return (
                <Markdown
                    className={classNames("text-sm", styles.agentMarkdown)}
                    options={AGENT_MARKDOWN_OPTIONS}
                >
                    {slateToPlainText(message.content)}
                </Markdown>
            );
        }
        return (
            <SlateRenderer
                content={message.content}
                onToggleCheck={onToggleCheck}
            />
        );
    }

    return <div className="text-sm whitespace-pre-wrap">{message.content}</div>;
};
